import { useMutation } from "@tanstack/react-query";
import { Checkbox, Col, Form, Input, InputNumber, Modal, Switch } from "antd";
import axios from "axios";
import configFile from "config.json";
import { getAuthContext } from "contexts/auth";
import { useContext, useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import styled from "styled-components";
import axiosCustom from "utils/axios";

const CreateConfigModal = ({ open, refetch, onCancel, propsData = {}}) => {
    
    const [form] = Form.useForm();
    const { jwtToken } = useContext(getAuthContext);

    const [data, setData] = useState([]);
    const [checkedList, setCheckedList] = useState([]);
    const [checkAll, setCheckAll] = useState(false);
    const [isIndeterminate, setIsIndeterminate] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (open) fetchData();
      }, [open]);        
    
    useEffect(() => {
        if (propsData) {
          form.setFieldsValue({
            menuItemDefID: propsData.menuItemDefID ?? "",
            miMasterObjNum: propsData.miMasterObjNum ?? "",
            price: propsData.overPrice ?? propsData.price,
            show: propsData?.show ?? true,
          });
        }
      }, [propsData, form]);
      
    
    // Modal хаахад form reset
    useEffect(() => {
        if (!open) form.resetFields();
    }, [open, form]);
    
    const fetchData = async () => {
        setIsLoading(true);
        try {
            const { data } = await axios.post(
                `${configFile.PROD_SERVER_URL}/store/list`,
                {},
                { headers: { Authorization: `Bearer ${jwtToken}` } }
            );

            const mappedResult = data.data.map((d) => ({
                value: d.storeName,
                label: d.storeNameMn ?? d.storeName,
            }));
            setData(mappedResult);
        } catch (error) {
            NotificationManager.error(
                error.response?.data?.errorMessage || "Алдаа гарлаа",
                "Error",
                5000
            );
        } finally {
            setIsLoading(false);
        }
    };

    const { mutate: createMenuConfig, isPending } = useMutation({
        mutationFn: async (payload) => {
          const response = await axiosCustom.post(
            `${configFile.PROD_SERVER_URL}/menu-config/upsert`,
            {
              storeName: checkedList,
              items: payload,
            },
            { headers: { Authorization: `Bearer ${jwtToken}` } }
          );
      
          const result = response.data;
      
          if (result?.data?.failed > 0) {
            const errorMsg =
              result.data.results?.[0]?.error || "Тодорхойгүй алдаа гарлаа";

            throw new Error(errorMsg);
          }
      
          return result;
        },
        
        onSuccess: async () => {
          NotificationManager.success("Амжилттай хадгалагдлаа!", "Success");
          form.resetFields();
          setCheckedList([]);
          setCheckAll(false);
          isPending && refetch();
          onCancel();
        },
      
        onError: (error) => {
          NotificationManager.error(
            error?.message || "Алдаа гарлаа",
            "Error"
          );
        },
      });
      

      const handleSave = async () => {
        try {
          const values = await form.validateFields();
      
          if (!checkedList.length) {
            NotificationManager.error("Салбараа сонгоно уу", "Error");
            return;
          }
      
          // const payloads = checkedList.map(() => ({
          //   menuItemDefID: propsData?.menuItemDefID,
          //   price: values.price,
          //   show: values?.show ?? true,
          // }));

          const payloads = [{
            menuItemDefID: propsData?.menuItemDefID,
            price: values.price,
            show: values?.show ?? true,
          }]
      
          createMenuConfig(payloads);
        } catch (e) {
          // ✅ AntD validate error
          const firstError =
            e?.errorFields?.[0]?.errors?.[0] ||
            "Form бөглөлтөд алдаа байна";
      
          NotificationManager.error(firstError, "Error", 5000);
          console.error("Validation error:", e);
        }
      };
      

    const onChange = (list) => {
        setCheckedList(list);
        setIsIndeterminate(!!list.length && list.length < data.length);
        setCheckAll(list.length === data.length);
    };

    const onCheckAllChange = (e) => {

        const allValues = e.target.checked ? data.map((item) => item.value) : [];
        setCheckedList(allValues);
        setIsIndeterminate(false);
        setCheckAll(e.target.checked);
    };

    return (
        <Modal
            title="Тохиргоо үүсгэх"
            open={open}
            onOk={handleSave}
            onCancel={onCancel}
            okText={isPending ? "Хадгалж байна..." : "Хадгалах"}
            cancelText="Болих"
            confirmLoading={isPending}
            width={700}
            centered
        >
            <StyledForm form={form} layout="vertical" initialValues={{ show: propsData?.show ?? true }}>
                <Form.Item
                    label="Салбар сонгох"
                    name="store"
                    rules={[{ required: true, message: "Салбараа сонгоно уу" }]}
                >
                    <div style={{ border: "1px solid #eee", padding: "12px", borderRadius: "8px" }}>
                        <Checkbox
                            indeterminate={isIndeterminate}
                            onChange={onCheckAllChange}
                            checked={checkAll}
                        >
                            Бүгдийг сонгох
                        </Checkbox>

                        <Checkbox.Group
                            style={{ display: "flex", flexWrap: "wrap", marginTop: 12 }}
                            value={checkedList}
                            onChange={onChange}
                        >
                            {data.map((item) => (
                                <Col span={8} key={item.value} style={{ marginBottom: 8 }}>
                                    <Checkbox value={item.value}>{item.label}</Checkbox>
                                </Col>
                            ))}
                        </Checkbox.Group>
                    </div>
                </Form.Item>

                <InfoCard>
                    <InfoItem>
                        <span>Бүтээгдэхүүний нэр</span>
                        <strong>{propsData?.masterName || "-"}</strong>
                    </InfoItem>

                    <InfoItem>
                        <span>Menu Item ID</span>
                        <strong>{propsData?.menuItemDefID || "-"}</strong>
                    </InfoItem>

                    <InfoItem>
                        <span>Pizza Base</span>
                        <strong>{propsData?.pizzaBase || "-"}</strong>
                    </InfoItem>

                    <InfoItem>
                        <span>Pizza Size</span>
                        <strong>{propsData?.pizzaSize || "-"}</strong>
                    </InfoItem>
                </InfoCard>

                <InputCard>
                    <InputLabel>Master Object Number</InputLabel>
                    <Form.Item 
                        name="miMasterObjNum"
                        rules={[{ required: true, message: "miMasterObjNum оруулна уу" }]}
                        style={{ margin: 0 }}
                    >
                        <Input readOnly placeholder="Жишээ: 4409009" />
                    </Form.Item>
                </InputCard>

                <InputCard>
                    <InputLabel>Үнэ оруулах</InputLabel>
                    <Form.Item 
                        name="price"
                        rules={[{ required: true, message: "Үнэ оруулна уу" }]}
                        style={{ margin: 0 }}
                    >
                        <InputNumber
                            style={{ width: "100%" }}
                            placeholder="Жишээ: 42000"
                            min={0}
                        />
                    </Form.Item>
                </InputCard>

                <Form.Item
                    label="Харагдах эсэх"
                    name="show"
                    valuePropName="checked"
                >
                    <SwitchRow>
                        <span style={{ fontWeight: 600 }}>Төлөв</span>

                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <Form.Item shouldUpdate noStyle>
                                {({ getFieldValue }) => {
                                    const isActive = getFieldValue("show");
                                    return (
                                        <Switch 
                                            size="small" 
                                            checked={isActive}
                                            onChange={(checked) => form.setFieldsValue({ show: checked })}
                                        />
                                    );
                                }}
                            </Form.Item>

                            <Form.Item shouldUpdate noStyle>
                                {({ getFieldValue }) => {
                                    const isActive = getFieldValue("show");
                                    return (
                                        <StatusBadge active={isActive}>
                                            {isActive ? "Харагдана" : "Харагдахгүй"}
                                        </StatusBadge>
                                    );
                                }}
                            </Form.Item>
                        </div>
                    </SwitchRow>
                </Form.Item>
            </StyledForm>
        </Modal>
    );
};

export default CreateConfigModal

const StyledForm = styled(Form)`
  .ant-form-item-label > label {
    display: flex;
    font-weight: 600;
    background: #f0f8ff;
    color: #333;
    padding: 6px 12px;
    border-radius: 6px;
    display: inline-block;
    margin-bottom: 4px;
  }

  .ant-form-item {
    background: #fafafa;
    padding: 12px;
    border-radius: 8px;
  }
`;
const InfoCard = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 16px;
`;

const InfoItem = styled.div`
  background: #ffffff;
  border: 1px solid #eaeaea;
  border-radius: 10px;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  span {
    font-size: 12px;
    color: #888;
    font-weight: 500;
  }

  strong {
    font-size: 14px;
    color: #222;
    font-weight: 600;
  }
`;

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  border: 1px solid #eaeaea;
  border-radius: 12px;
  padding: 12px 16px;
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ active }) => (active ? "#e6fffb" : "#fff1f0")};
  color: ${({ active }) => (active ? "#08979c" : "#cf1322")};
`;
const InputCard = styled.div`
  background: #ffffff;
  border: 1px solid #eaeaea;
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

const InputLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #666;
  margin-bottom: 4px;
`;
